import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DateTimePicker } from "@progress/kendo-react-dateinputs";
import { Popup } from "@progress/kendo-react-popup";
import { getUserStatus, updateUserAvailabilityStatus } from "../../../../actions/userProfile";
import { icon, label } from "../../../../config";
import { button } from "../../../../config/constants";
import "./SetAwayStatusModal.scss";

/**
 * SetAwayStatusModal component for setting user status with custom messages and scheduling options
 * @author Muskan Thakur
 */
const SetAwayStatusModal = ({ show, onClose, anchor, userId, onStatusUpdate }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const targetUserId = userId || user?.id;
  const [customStatus, setCustomStatus] = useState("");
  const [selectedDuration, setSelectedDuration] = useState("4 hours");
  const [showDateTimePicker, setShowDateTimePicker] = useState(false);
  const [showFromToPickers, setShowFromToPickers] = useState(false);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [awayFrom, setAwayFrom] = useState(null);
  const [awayTo, setAwayTo] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState(label.AVAILABLE);
  const [timePresets, setTimePresets] = useState([]);
  const [selectedTimePresetId, setSelectedTimePresetId] = useState(null);
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const hasFetchedTimePresets = useRef(false);
  const justOpenedRef = useRef(false);
  const lastClickTargetRef = useRef(null);

  /**
   * Helper function to get tomorrow morning at 9 AM
   * @returns {Date} Tomorrow at 9:00 AM
   */
  const getTomorrowMorning = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(9, 0, 0, 0);
    return tomorrow;
  };

  /**
   * Helper function to get default "To" date (24 hours from "From")
   * @param {Date} fromDate - The "From" date
   * @returns {Date} "To" date (24 hours later)
   */
  const getDefaultToDate = (fromDate) => {
    const defaultTo = new Date(fromDate || getTomorrowMorning());
    defaultTo.setHours(defaultTo.getHours() + 24);
    return defaultTo;
  };

  /**
   * Helper function to ensure a date is not in the past
   * @author Muskan Thakur
   */
  const ensureNotPastDate = (date, minDate = null) => {
    const now = minDate || new Date();
    if (!date) return null;
    const dateToCheck = new Date(date);
    return dateToCheck < now ? now : dateToCheck;
  };

  /**
   * Helper function to get current time rounded to the next minute
   * @author Muskan Thakur
   */
  const getCurrentTimeRounded = () => {
    const now = new Date();
    now.setSeconds(0, 0);
    now.setMinutes(now.getMinutes() + 1);
    return now;
  };

  /* Initialize modal with user status when it opens
   */
  useEffect(() => {
    if (show && targetUserId) {
      /* Fetch user status for the target user
       */
      (async () => {
        const statusData = await dispatch(getUserStatus(targetUserId));
        if (statusData?.timePresets && !hasFetchedTimePresets.current) {
          setTimePresets(statusData.timePresets);
          hasFetchedTimePresets.current = true;
        } else if (statusData?.timePresets) {
          setTimePresets(statusData.timePresets);
        }
        if (statusData?.userStatus) {
          const currentStatus = statusData.userStatus.availabilityStatus || statusData.userStatus.StatusText || label.AVAILABLE;
          const currentCustomStatus = statusData.userStatus.customStatus || "";
          setSelectedStatus(currentStatus);
          setCustomStatus(currentCustomStatus);

          // Use TimePresetId to find and set the selected duration option
          const timePresetId = statusData.userStatus.TimePresetId;
          if (timePresetId && statusData.timePresets) {
            const selectedPreset = statusData.timePresets.find((preset) => preset.Id === timePresetId);
            if (selectedPreset) {
              setSelectedDuration(selectedPreset.Name);
              setSelectedTimePresetId(timePresetId);

              // If it's "Away from duration (From - To)" or "Choose date & time", show the appropriate pickers
              const presetName = selectedPreset.Name;
              if (presetName === "Away from duration (From - To)" || presetName === "Away for Duration (From - To)") {
                let validatedFromDate;
                if (statusData.userStatus.StartsAt) {
                  validatedFromDate = ensureNotPastDate(new Date(statusData.userStatus.StartsAt)) || getTomorrowMorning();
                } else {
                  validatedFromDate = getTomorrowMorning();
                }
                setAwayFrom(validatedFromDate);

                let validatedToDate;
                if (statusData.userStatus.ExpiresAt) {
                  validatedToDate =
                    ensureNotPastDate(new Date(statusData.userStatus.ExpiresAt), validatedFromDate) || getDefaultToDate(validatedFromDate);
                } else {
                  validatedToDate = getDefaultToDate(validatedFromDate);
                }
                setAwayTo(validatedToDate);
                setShowFromToPickers(true);
              } else if (presetName === "Choose date & time" || presetName === "Choose Date & Time") {
                if (statusData.userStatus.ExpiresAt) {
                  const dateTime = ensureNotPastDate(new Date(statusData.userStatus.ExpiresAt)) || getCurrentTimeRounded();
                  setSelectedDateTime(dateTime);
                } else {
                  setSelectedDateTime(getCurrentTimeRounded());
                }
                setShowDateTimePicker(true);
              }
            } else {
              // TimePresetId exists but preset not found - fallback to default
              if (currentStatus === label.AWAY) {
                const fourHoursPreset = statusData.timePresets?.find((preset) => preset.Name === "4 hours");
                if (fourHoursPreset) {
                  setSelectedDuration(fourHoursPreset.Name);
                  setSelectedTimePresetId(fourHoursPreset.Id);
                } else {
                  setSelectedDuration("4 hours");
                }
              }
            }
          } else if (currentStatus === label.AWAY) {
            /* Set default duration to 4 hours when Away status is selected and no TimePresetId
             */
            const fourHoursPreset = statusData.timePresets?.find((preset) => preset.Name === "4 hours");
            if (fourHoursPreset) {
              setSelectedDuration(fourHoursPreset.Name);
              setSelectedTimePresetId(fourHoursPreset.Id);
            } else {
              setSelectedDuration("4 hours");
            }
          }
        } else {
          /* Fallback to logged-in user's data if userId matches current user
           */
          if (targetUserId === user?.id || targetUserId === user?.id?.toString()) {
            const currentStatus = user?.availabilityStatus || label.AVAILABLE;
            const currentCustomStatus = user?.customStatus || "";
            setSelectedStatus(currentStatus);
            setCustomStatus(currentCustomStatus);

            if (user?.scheduledFrom && user?.scheduledTo) {
              const validatedFrom = ensureNotPastDate(new Date(user.scheduledFrom)) || getTomorrowMorning();
              const validatedTo = ensureNotPastDate(new Date(user.scheduledTo), validatedFrom) || getDefaultToDate(validatedFrom);
              setAwayFrom(validatedFrom);
              setAwayTo(validatedTo);
              // Try to find the "Away from duration (From - To)" preset
              const fromToPreset = timePresets?.find((preset) => preset.Name === "Away from duration (From - To)");
              if (fromToPreset) {
                setSelectedDuration(fromToPreset.Name);
                setSelectedTimePresetId(fromToPreset.Id);
              } else {
                setSelectedDuration("Away for Duration From - To");
              }
              setShowFromToPickers(true);
            } else if (currentStatus === label.AWAY) {
              const fourHoursPreset = timePresets?.find((preset) => preset.Name === "4 hours");
              if (fourHoursPreset) {
                setSelectedDuration(fourHoursPreset.Name);
                setSelectedTimePresetId(fourHoursPreset.Id);
              } else {
                setSelectedDuration("4 hours");
              }
            }
          } else {
            /* Default values for new user
             */
            setSelectedStatus(label.AVAILABLE);
            setCustomStatus("");
            const fourHoursPreset = timePresets?.find((preset) => preset.Name === "4 hours");
            if (fourHoursPreset) {
              setSelectedDuration(fourHoursPreset.Name);
              setSelectedTimePresetId(fourHoursPreset.Id);
            } else {
              setSelectedDuration("4 hours");
            }
          }
        }
      })();
    } else if (!show) {
      /* Reset when modal closes
       */
      setCustomStatus("");
      const fourHoursPreset = timePresets?.find((preset) => preset.Name === "4 hours");
      if (fourHoursPreset) {
        setSelectedDuration(fourHoursPreset.Name);
        setSelectedTimePresetId(fourHoursPreset.Id);
      } else {
        setSelectedDuration("4 hours");
        setSelectedTimePresetId(null);
      }
      setSelectedDateTime(null);
      setAwayFrom(null);
      setAwayTo(null);
      setShowDateTimePicker(false);
      setShowFromToPickers(false);
      setSelectedStatus(label.AVAILABLE);
      hasFetchedTimePresets.current = false;
    }
  }, [show, targetUserId, dispatch, user]);

  // Map timePresets to duration options format
  const getDurationOptions = () => {
    if (!timePresets || timePresets.length === 0) {
      // Fallback to default options if API hasn't loaded yet
      return [
        { label: "30 minutes", value: "30 minutes", minutes: 30 },
        { label: "1 hour", value: "1 hour", minutes: 60 },
        { label: "4 hours", value: "4 hours", minutes: 240 },
        { label: "Today", value: "Today", type: "today" },
        { label: "This week", value: "This week", type: "week" },
        { label: "Choose Date & Time", value: "Choose Date & Time", type: "custom" },
        { label: "Away for Duration (From - To)", value: "Away for Duration From - To", type: "fromTo" },
      ];
    }
    return timePresets.map((preset) => {
      const name = preset.Name;
      // Map preset names to our internal types
      let type = null;
      let minutes = null;
      if (name === "30 minutes") {
        minutes = 30;
      } else if (name === "1 hour") {
        minutes = 60;
      } else if (name === "4 hours") {
        minutes = 240;
      } else if (name === "Today") {
        type = "today";
      } else if (name === "This week") {
        type = "week";
      } else if (name === "Choose date & time" || name === "Choose Date & Time") {
        type = "custom";
      } else if (name === "Away from duration (From - To)" || name === "Away for Duration (From - To)") {
        type = "fromTo";
      }
      let displayLabel = name;
      if (name === "Away from duration (From - To)") {
        displayLabel = "Duration (From - To)";
      }
      
      return {
        label: displayLabel,
        value: name,
        id: preset.Id,
        type: type,
        minutes: minutes,
      };
    });
  };

  const durationOptions = getDurationOptions();

  useEffect(() => {
    const selectedOption = durationOptions.find((opt) => opt.value === selectedDuration);
    if (selectedOption?.id) {
      setSelectedTimePresetId(selectedOption.id);
    }

    const selectedOptionType = selectedOption?.type;
    if (selectedOptionType === "custom") {
      setShowDateTimePicker(true);
      setShowFromToPickers(false);

      if (!selectedDateTime) {
        setSelectedDateTime(getCurrentTimeRounded());
      } else {
        /*
         * Ensure selectedDateTime is not in the past
         */
        const validatedDateTime = ensureNotPastDate(selectedDateTime);
        if (validatedDateTime && validatedDateTime.getTime() !== selectedDateTime.getTime()) {
          setSelectedDateTime(validatedDateTime);
        }
      }
    } else if (selectedOptionType === "fromTo") {
      setShowFromToPickers(true);
      setShowDateTimePicker(false);

      if (!awayFrom) {
        setAwayFrom(getTomorrowMorning());
      } else {
        /*
         * Ensure awayFrom is not in the past
         */
        const validatedFrom = ensureNotPastDate(awayFrom);
        if (validatedFrom && validatedFrom.getTime() !== awayFrom.getTime()) {
          setAwayFrom(validatedFrom);
        }
      }
      if (!awayTo) {
        setAwayTo(getDefaultToDate(awayFrom || getTomorrowMorning()));
      } else {
        /*
         * Ensure awayTo is not in the past and is after awayFrom
         */
        const minToDate = awayFrom || getTomorrowMorning();
        const validatedTo = ensureNotPastDate(awayTo, minToDate);
        if (validatedTo && validatedTo.getTime() !== awayTo.getTime()) {
          setAwayTo(validatedTo);
        } else if (awayTo < minToDate) {
          setAwayTo(getDefaultToDate(minToDate));
        }
      }
    } else {
      setShowDateTimePicker(false);
      setShowFromToPickers(false);
    }
  }, [selectedDuration, durationOptions]);

  const handleDurationSelect = (option) => {
    setSelectedDuration(option.value);
    setSelectedTimePresetId(option.id);
    setIsDropdownOpen(false);
  };

  const handleSave = async () => {
    let clearAwayAt = null;
    let scheduledFrom = null;
    let scheduledTo = null;

    /* Handle scheduling options
     */
    const selectedOption = durationOptions.find((opt) => opt.value === selectedDuration);
    const selectedOptionType = selectedOption?.type;

    if (selectedOptionType === "custom") {
      /* Use selectedDateTime or default to current time, ensuring it's not in the past
       */
      const dateTime = selectedDateTime || getCurrentTimeRounded();
      clearAwayAt = ensureNotPastDate(dateTime) || getCurrentTimeRounded();
    } else if (selectedOptionType === "fromTo") {
      const fromDate = awayFrom || getTomorrowMorning();
      const validatedFrom = ensureNotPastDate(fromDate) || getTomorrowMorning();
      const toDate = awayTo || getDefaultToDate(validatedFrom);
      const validatedTo = ensureNotPastDate(toDate, validatedFrom) || getDefaultToDate(validatedFrom);
      scheduledFrom = validatedFrom;
      scheduledTo = validatedTo;

      clearAwayAt = validatedTo;
    } else if (selectedDuration && selectedOption) {
      if (selectedOption.minutes) {
        clearAwayAt = new Date(Date.now() + selectedOption.minutes * 60 * 1000);
      } else if (selectedOption.type === "today") {
        const today = new Date();
        today.setHours(23, 59, 59, 999);
        clearAwayAt = today;
      } else if (selectedOption.type === "week") {
        const weekEnd = new Date();
        weekEnd.setDate(weekEnd.getDate() + (7 - weekEnd.getDay()));
        weekEnd.setHours(23, 59, 59, 999);
        clearAwayAt = weekEnd;
      }
    }

    let statusToSet;
    if (selectedOptionType === "fromTo" && scheduledFrom) {
      const fromDate = new Date(scheduledFrom);
      const now = new Date();
      if (fromDate > now) {
        statusToSet = label.AVAILABLE;
      } else {
        statusToSet = selectedStatus === label.AWAY ? label.AWAY : label.AVAILABLE;
      }
    } else {
      statusToSet = selectedStatus === label.AWAY ? label.AWAY : label.AVAILABLE;
    }

    await dispatch(
      updateUserAvailabilityStatus(
        targetUserId,
        statusToSet,
        clearAwayAt,
        customStatus.trim() || null,
        scheduledFrom,
        scheduledTo,
        selectedTimePresetId
      )
    );

    if (onStatusUpdate) {
      onStatusUpdate(statusToSet);
    }
    onClose();
  };

  const handleCancel = () => {
    setCustomStatus("");
    const fourHoursPreset = timePresets?.find((preset) => preset.Name === "4 hours");
    if (fourHoursPreset) {
      setSelectedDuration(fourHoursPreset.Name);
      setSelectedTimePresetId(fourHoursPreset.Id);
    } else {
      setSelectedDuration("4 hours");
      setSelectedTimePresetId(null);
    }
    setSelectedDateTime(null);
    setAwayFrom(null);
    setAwayTo(null);
    setShowDateTimePicker(false);
    setShowFromToPickers(false);
    setSelectedStatus(label.AVAILABLE);
    onClose();
  };

  /**
   * Handle click outside to close dropdown (but not the modal)
   * @author Muskan Thakur
   */
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if we just opened the dropdown
      if (justOpenedRef.current) {
        return;
      }

      // Don't close if this is the same click that opened the dropdown
      if (
        lastClickTargetRef.current &&
        (event.target === lastClickTargetRef.current ||
          event.target.contains(lastClickTargetRef.current) ||
          lastClickTargetRef.current.contains(event.target))
      ) {
        return;
      }

      // Don't close if clicking on the dropdown button itself
      if (
        dropdownButtonRef.current &&
        (dropdownButtonRef.current.contains(event.target) || dropdownButtonRef.current === event.target || event.target.closest(".duration-dropdown"))
      ) {
        return;
      }

      // Don't close if clicking inside the dropdown options
      if (dropdownRef.current && dropdownRef.current.contains(event.target)) {
        return;
      }

      // Don't close dropdown if clicking on DateTimePicker or its popup elements
      const isDateTimePickerElement =
        event.target.closest(".k-datetimepicker") ||
        event.target.closest(".k-dateinput") ||
        event.target.closest(".k-calendar") ||
        event.target.closest(".k-timepicker") ||
        event.target.closest(".k-popup") ||
        event.target.closest(".k-widget") ||
        event.target.closest(".k-calendar-container") ||
        event.target.closest(".k-animation-container");

      if (!isDateTimePickerElement) {
        setIsDropdownOpen(false);
      }
    };

    if (isDropdownOpen) {
      // Use click event instead of mousedown to avoid timing issues
      // Use a longer delay to ensure dropdown button clicks are processed first and flag is set
      const timeoutId = setTimeout(() => {
        document.addEventListener("click", handleClickOutside, false);
      }, 300);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("click", handleClickOutside, false);
      };
    }
  }, [isDropdownOpen]);

  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  const handleDropdownToggle = (e) => {
    e.stopPropagation();

    // Store the click target to prevent click outside handler from firing on same click
    lastClickTargetRef.current = e.target;

    // Close any open Kendo DateTimePicker popups to prevent interference
    const activeInput = document.activeElement;
    if (activeInput && (activeInput.classList.contains("k-input") || activeInput.closest(".k-datetimepicker"))) {
      activeInput.blur();
    }

    // Toggle dropdown immediately
    const willBeOpen = !isDropdownOpen;

    // Set flag to prevent immediate close BEFORE opening
    if (willBeOpen) {
      justOpenedRef.current = true;
      // Keep the flag true longer to prevent immediate close
      setTimeout(() => {
        justOpenedRef.current = false;
        lastClickTargetRef.current = null;
      }, 300);
    }

    setIsDropdownOpen(willBeOpen);
  };

  if (!show) return null;

  return (
    <Popup anchor={anchor?.current} show={show} className={"dt-popup set-away-status-popup"} align={{ horizontal: "left", vertical: "bottom" }}>
      <div className='set-away-status-modal' onClick={handleModalClick}>
        <div className='dt-popup-header d-flex justify-content-between align-items-center pt-2 pb-2 pl-3 pr-3'>
          <div className='dt-popup-title'>Set a status</div>
          <span onClick={handleCancel} className="dt-popup-cancel-btn">
            {icon.CLOSE}
          </span>
        </div>
        <div className='dt-popup-body'>
          <div className='modal-body p-0'>
            <div className='status-input-wrapper'>
              <input
                type='text'
                className='status-input'
                placeholder="What's your status?"
                value={customStatus}
                onChange={(e) => setCustomStatus(e.target.value)}
              />
            </div>
            <div className='status-toggle-wrapper'>
              <label className='status-toggle-label'>Availability</label>
              <div className='status-toggle-buttons'>
                <button
                  type='button'
                  className={`btn btn-sm status-toggle-btn ${selectedStatus === label.AVAILABLE ? "btn-success" : ""}`}
                  onClick={() => setSelectedStatus(label.AVAILABLE)}>
                  {label.AVAILABLE}
                </button>
                <button
                  type='button'
                  className={`btn btn-sm status-toggle-btn ${selectedStatus === label.AWAY ? "btn-tertiary" : ""}`}
                  onClick={() => setSelectedStatus(label.AWAY)}>
                  {label.AWAY}
                </button>
              </div>
            </div>
            {selectedStatus === label.AWAY && (
              <>
                <label className='duration-label'>Away for</label>
                <div className='duration-dropdown-wrapper' ref={dropdownRef}>
                  <div
                    ref={dropdownButtonRef}
                    className='duration-dropdown'
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownToggle(e);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}>
                    <span>{selectedDuration === "Away from duration (From - To)" ? "Duration (From - To)" : selectedDuration}</span>
                    <span className='dropdown-arrow'>{icon.ARROW_DROPDOWN_ICON}</span>
                  </div>
                  {isDropdownOpen && (
                    <div
                      className='duration-options'
                      onClick={(e) => e.stopPropagation()}
                      onMouseDown={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                      }}>
                      {durationOptions.map((option) => (
                        <div
                          key={option.value}
                          className={`duration-option ${selectedDuration === option.value ? "selected" : ""}`}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            handleDurationSelect(option);
                          }}>
                          {option.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {showDateTimePicker && (
                  <div className='date-time-picker-wrapper'>
                    <label className='date-time-label'>Time</label>
                    <DateTimePicker
                      value={selectedDateTime || new Date()}
                      onChange={(e) => setSelectedDateTime(e.value)}
                      format='dd MMM yyyy, HH:mm'
                      min={getCurrentTimeRounded()}
                      width='100%'
                      size='small'
                    />
                  </div>
                )}
                {showFromToPickers && (
                  <div className='from-to-pickers-wrapper'>
                    <div className='date-time-picker-wrapper'>
                      <label className='date-time-label'>From</label>
                      <DateTimePicker
                        value={awayFrom || getTomorrowMorning()}
                        onChange={(e) => {
                          setAwayFrom(e.value);
                          if (e.value && (!awayTo || awayTo.getTime() === getDefaultToDate(awayFrom).getTime())) {
                            setAwayTo(getDefaultToDate(e.value));
                          }
                        }}
                        format='dd MMM yyyy, HH:mm'
                        min={new Date()}
                        width='100%'
                        size='small'
                      />
                    </div>
                    <div className='date-time-picker-wrapper'>
                      <label className='date-time-label'>To</label>
                      <DateTimePicker
                        value={awayTo || getDefaultToDate(awayFrom)}
                        onChange={(e) => setAwayTo(e.value)}
                        format='dd MMM yyyy, HH:mm'
                        min={awayFrom || getTomorrowMorning()}
                        width='100%'
                        size='small'
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className='dt-popup-footer'>
          <div className='modal-footer p-0 border-none'>
            <button className='btn btn-ghost' onClick={handleCancel}>
              {button.CANCEL}
            </button>
            <button className='btn btn-primary' onClick={handleSave}>
              Set
            </button>
          </div>
        </div>
      </div>
    </Popup>
  );
};

export default SetAwayStatusModal;
